import { useState, useEffect, useCallback, useRef } from 'react'

const NAVY = '#002E53'
const GOLD = '#C5A059'
const FONT = "'Poppins', sans-serif"

function AdminTeamForm({ token, member, onClose }) {
  const [formData, setFormData] = useState({
    id: member?.id || '',
    name: member?.name || '',
    role: member?.role || '',
    whatsapp: member?.whatsapp || '',
    instagram: member?.instagram || '',
    facebook: member?.facebook || '',
    tiktok: member?.tiktok || '',
    threads: member?.threads || '',
    linkedin: member?.linkedin || '',
    x: member?.x || '',
    languages: member?.languages ? member.languages.join(', ') : '',
    fullBio: member?.fullBio || '',
  })
  
  const [imageFile, setImageFile] = useState(null)
  const [imagePreview, setImagePreview] = useState(member?.image || null)
  const [loading, setLoading] = useState(false)
  const fileInputRef = useRef(null)

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0]
      setImageFile(file)
      setImagePreview(URL.createObjectURL(file))
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)

    const fd = new FormData()
    Object.keys(formData).forEach(key => {
      let val = formData[key]
      if (key === 'languages' && val) {
        val = val.replace(/\s+(and|&)\s+/gi, ', ').replace(/\./g, ', ')
      }
      fd.append(key, val)
    })
    
    if (imageFile) {
      fd.append('image', imageFile)
    }

    try {
      const url = member ? `/api/team/${member.id}` : '/api/team'
      const method = member ? 'PUT' : 'POST'

      const res = await fetch(url, {
        method,
        headers: { 'x-admin-token': token },
        body: fd
      })

      if (!res.ok) throw new Error('Failed to save team member')
      onClose(true)
    } catch (err) {
      alert(err.message)
    } finally {
      setLoading(false)
    }
  }

  const inputStyle = {
    width: '100%', padding: '12px 14px', borderRadius: '8px', border: '1.5px solid #eaeef3',
    fontFamily: FONT, fontSize: '14px', color: NAVY, outline: 'none', transition: 'border-color 0.2s',
    background: '#f8fafb'
  }

  const labelStyle = {
    display: 'block', fontFamily: FONT, fontSize: '12px', fontWeight: 600, color: '#64748b',
    marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px'
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,18,36,0.75)', backdropFilter: 'blur(8px)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', overflowY: 'auto' }}>
      <div style={{ background: '#fff', borderRadius: '20px', width: '100%', maxWidth: '800px', maxHeight: '90vh', overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
        
        {/* Header */}
        <div style={{ padding: '20px 24px', borderBottom: `2px solid ${GOLD}`, background: `linear-gradient(135deg, ${NAVY} 0%, #001a33 100%)`, borderRadius: '20px 20px 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ fontFamily: FONT, fontSize: '18px', fontWeight: 700, color: '#fff', margin: 0 }}>
            {member ? 'Edit Team Member' : 'Add New Team Member'}
          </h2>
          <button onClick={() => onClose()} style={{ width: '32px', height: '32px', borderRadius: '50%', background: 'rgba(255,255,255,0.1)', border: 'none', color: '#fff', fontSize: '18px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
          
          <div style={{ display: 'grid', gridTemplateColumns: '150px 1fr', gap: '24px' }}>
            {/* Image Upload */}
            <div>
              <label style={labelStyle}>Profile Photo</label>
              <div 
                onClick={() => fileInputRef.current?.click()}
                style={{ 
                  width: '150px', height: '150px', borderRadius: '12px', border: '2px dashed #cbd5e1', 
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer', background: '#f8fafb', overflow: 'hidden', position: 'relative'
                }}
              >
                {imagePreview ? (
                  <img src={imagePreview} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <>
                    <span style={{ fontSize: '32px', marginBottom: '8px' }}>📷</span>
                    <span style={{ fontFamily: FONT, fontSize: '12px', color: '#94a3b8' }}>Upload Photo</span>
                  </>
                )}
              </div>
              <input type="file" accept="image/*" ref={fileInputRef} onChange={handleImageChange} style={{ display: 'none' }} />
            </div>

            {/* Basic Info */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div>
                <label style={labelStyle}>Full Name *</label>
                <input required name="name" value={formData.name} onChange={handleChange} style={inputStyle} placeholder="John Doe" />
              </div>
              <div>
                <label style={labelStyle}>Role / Title</label>
                <input name="role" value={formData.role} onChange={handleChange} style={inputStyle} placeholder="Property Consultant" />
              </div>
              <div style={{ gridColumn: 'span 2' }}>
                <label style={labelStyle}>
                  Languages <span style={{ textTransform: 'none', fontWeight: 400, color: '#94a3b8', fontSize: '11px' }}>(Comma separated only)</span>
                </label>
                <input name="languages" value={formData.languages} onChange={handleChange} style={inputStyle} placeholder="English, Arabic, Hindi" />
              </div>
            </div>
          </div>

          <div style={{ height: '1px', background: '#eaeef3' }} />

          {/* Contact & Socials */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
            <div>
              <label style={labelStyle}>WhatsApp Number</label>
              <input name="whatsapp" value={formData.whatsapp} onChange={handleChange} style={inputStyle} placeholder="971501234567" />
            </div>
            <div>
              <label style={labelStyle}>Instagram URL</label>
              <input name="instagram" value={formData.instagram} onChange={handleChange} style={inputStyle} placeholder="https://instagram.com/..." />
            </div>
            <div>
              <label style={labelStyle}>Facebook URL</label>
              <input name="facebook" value={formData.facebook} onChange={handleChange} style={inputStyle} placeholder="https://facebook.com/..." />
            </div>
            <div>
              <label style={labelStyle}>TikTok URL</label>
              <input name="tiktok" value={formData.tiktok} onChange={handleChange} style={inputStyle} placeholder="https://tiktok.com/..." />
            </div>
            <div>
              <label style={labelStyle}>Threads URL</label>
              <input name="threads" value={formData.threads} onChange={handleChange} style={inputStyle} placeholder="https://threads.net/..." />
            </div>
            <div>
              <label style={labelStyle}>LinkedIn URL</label>
              <input name="linkedin" value={formData.linkedin} onChange={handleChange} style={inputStyle} placeholder="https://linkedin.com/..." />
            </div>
            <div>
              <label style={labelStyle}>X (Twitter) URL</label>
              <input name="x" value={formData.x} onChange={handleChange} style={inputStyle} placeholder="https://x.com/..." />
            </div>
          </div>

          <div style={{ height: '1px', background: '#eaeef3' }} />

          {/* Bios */}
          <div>
            <label style={labelStyle}>Biography (Detail Page & Marquee) *</label>
            <textarea required name="fullBio" value={formData.fullBio} onChange={handleChange} style={{ ...inputStyle, minHeight: '160px', resize: 'vertical' }} placeholder="Complete professional background..." />
          </div>

          {/* Footer Buttons */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '10px' }}>
            <button type="button" onClick={() => onClose()} style={{ padding: '12px 24px', borderRadius: '10px', border: '1px solid #cbd5e1', background: '#fff', color: NAVY, fontFamily: FONT, fontSize: '14px', fontWeight: 600, cursor: 'pointer' }}>
              Cancel
            </button>
            <button type="submit" disabled={loading} style={{ padding: '12px 32px', borderRadius: '10px', border: 'none', background: GOLD, color: '#fff', fontFamily: FONT, fontSize: '14px', fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer' }}>
              {loading ? 'Saving...' : 'Save Member'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default function AdminTeamPanel({ token }) {
  const [members, setMembers] = useState([])
  const [loading, setLoading] = useState(true)
  const [editingMember, setEditingMember] = useState(null)
  const [isFormOpen, setIsFormOpen] = useState(false)
  const [deleteConfirm, setDeleteConfirm] = useState(null)

  const fetchMembers = useCallback(async () => {
    setLoading(true)
    try {
      const res = await fetch('/api/team', { headers: { 'x-admin-token': token } })
      const data = await res.json()
      setMembers(Array.isArray(data) ? data : [])
    } catch {
      setMembers([])
    } finally {
      setLoading(false)
    }
  }, [token])

  useEffect(() => { fetchMembers() }, [fetchMembers])

  const handleDelete = async () => {
    if (!deleteConfirm) return
    try {
      await fetch(`/api/team/${deleteConfirm}`, { method: 'DELETE', headers: { 'x-admin-token': token } })
      setDeleteConfirm(null)
      fetchMembers()
    } catch (err) {
      alert('Failed to delete member')
    }
  }

  const handleEdit = (member) => {
    setEditingMember(member)
    setIsFormOpen(true)
  }

  const handleAddNew = () => {
    setEditingMember(null)
    setIsFormOpen(true)
  }

  const handleCloseForm = (refresh) => {
    setIsFormOpen(false)
    setEditingMember(null)
    if (refresh) fetchMembers()
  }

  if (isFormOpen) {
    return <AdminTeamForm token={token} member={editingMember} onClose={handleCloseForm} />
  }

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
      <div style={{ width: '40px', height: '40px', border: `3px solid ${GOLD}30`, borderTopColor: GOLD, borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
    </div>
  )

  return (
    <div style={{ padding: 'clamp(16px,3vw,28px)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h3 style={{ fontFamily: FONT, fontSize: '18px', fontWeight: 700, color: NAVY, margin: '0 0 4px' }}>Team Members</h3>
          <p style={{ fontFamily: FONT, fontSize: '13px', color: '#64748b', margin: 0 }}>{members.length} member{members.length !== 1 ? 's' : ''}</p>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button onClick={fetchMembers} style={{ padding: '9px 16px', borderRadius: '8px', border: `1.5px solid ${GOLD}40`, background: `${GOLD}08`, color: NAVY, fontFamily: FONT, fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>
            ↻ Refresh
          </button>
          <button onClick={handleAddNew} style={{ padding: '9px 16px', borderRadius: '8px', border: 'none', background: NAVY, color: '#fff', fontFamily: FONT, fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>
            + Add New Member
          </button>
        </div>
      </div>

      {members.length === 0 ? (
        <div style={{ background: '#fff', borderRadius: '16px', padding: '80px 20px', textAlign: 'center', boxShadow: '0 2px 12px rgba(0,46,83,0.06)' }}>
          <div style={{ fontSize: '52px', marginBottom: '16px' }}>👥</div>
          <h3 style={{ fontFamily: FONT, fontSize: '18px', fontWeight: 700, color: NAVY, margin: '0 0 8px' }}>No team members</h3>
          <p style={{ fontFamily: FONT, fontSize: '14px', color: '#94a3b8', margin: 0 }}>Add your first team member.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {members.map(m => (
            <div key={m.id} style={{ background: '#fff', borderRadius: '14px', padding: '16px 20px', boxShadow: '0 1px 6px rgba(0,46,83,0.06)', display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}>
              <div style={{ width: '60px', height: '60px', borderRadius: '50%', overflow: 'hidden', background: '#f0f4f8', flexShrink: 0 }}>
                {m.image ? (
                  <img src={m.image} alt={m.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px' }}>👤</div>
                )}
              </div>
              <div style={{ flex: 1, minWidth: '140px' }}>
                <div style={{ fontFamily: FONT, fontSize: '16px', fontWeight: 700, color: NAVY, marginBottom: '2px' }}>{m.name}</div>
                <div style={{ fontFamily: FONT, fontSize: '13px', color: '#64748b' }}>
                  {m.role} {m.languages?.length ? `· Speaks: ${m.languages.join(', ')}` : ''}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '6px' }}>
                <button onClick={() => handleEdit(m)} style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid #eaeef3', background: '#f8fafb', color: NAVY, fontFamily: FONT, fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}>Edit</button>
                <button onClick={() => setDeleteConfirm(m.id)} style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid #fca5a5', background: 'transparent', color: '#dc2626', fontFamily: FONT, fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Custom Delete Confirmation Modal */}
      {deleteConfirm && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,16,32,0.6)', backdropFilter: 'blur(4px)' }}>
          <div style={{ background: '#fff', borderRadius: '16px', padding: '32px', maxWidth: '400px', width: '90%', boxShadow: '0 20px 40px rgba(0,0,0,0.2)', border: `1px solid #fca5a5`, animation: 'fadeIn 0.2s ease' }}>
            <div style={{ fontSize: '36px', marginBottom: '16px', textAlign: 'center' }}>🗑️</div>
            <h3 style={{ fontFamily: FONT, fontSize: '20px', fontWeight: 700, color: NAVY, textAlign: 'center', margin: '0 0 12px' }}>Delete Team Member?</h3>
            <p style={{ fontFamily: FONT, fontSize: '14px', color: '#64748b', textAlign: 'center', margin: '0 0 24px', lineHeight: 1.6 }}>Are you sure you want to permanently delete this team member? This action cannot be undone.</p>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button onClick={() => setDeleteConfirm(null)} style={{ flex: 1, padding: '12px', borderRadius: '10px', border: '1px solid #d1d5db', background: '#fff', color: NAVY, fontFamily: FONT, fontSize: '14px', fontWeight: 600, cursor: 'pointer', transition: 'background 0.2s' }} onMouseEnter={e => e.currentTarget.style.background = '#f8fafb'} onMouseLeave={e => e.currentTarget.style.background = '#fff'}>Cancel</button>
              <button onClick={handleDelete} style={{ flex: 1, padding: '12px', borderRadius: '10px', border: 'none', background: '#dc2626', color: '#fff', fontFamily: FONT, fontSize: '14px', fontWeight: 600, cursor: 'pointer', boxShadow: '0 4px 12px rgba(220,38,38,0.2)', transition: 'background 0.2s' }} onMouseEnter={e => e.currentTarget.style.background = '#b91c1c'} onMouseLeave={e => e.currentTarget.style.background = '#dc2626'}>Yes, Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
